import { EmailTemplate } from 'screens/mass-mail/types';

/**
 * Quick reply templates for incasso (debt collection) communication.
 * Injected as contextTemplates into mass-mail when navigating from a dossier.
 *
 * Available context placeholders (set via state.contextPlaceholders in DossierDetailSheet):
 *   {ticketId}      e.g. "INC-77"
 *   {partnerName}   the customer's display name
 *   {buildingName}  cached building name
 *   {totalDebt}     formatted euro amount, e.g. "€ 1.234,56"
 */
export const INCASSO_EMAIL_TEMPLATES: EmailTemplate[] = [
  {
    id: 'incasso_first_reminder',
    subject: 'Betalingsherinnering — openstaande facturen [{ticketId}]',
    content: `<p>Geachte {contact.firstName} {contact.lastName},</p>

<p>Bij nazicht van uw klantenrekening stellen wij vast dat er op dit ogenblik een openstaand saldo van <strong>{totalDebt}</strong> bestaat met betrekking tot het pand <strong>{buildingName}</strong>. Een gedetailleerd overzicht van de betrokken facturen vindt u in bijlage.</p>

<p>Mogelijk werd één of meerdere facturen volledig of gedeeltelijk niet vereffend, of werd een betaling niet correct toegewezen. Indien u de betaling reeds heeft uitgevoerd, mag u deze brief als onbestaande beschouwen en verzoeken wij u vriendelijk ons het betalingsbewijs over te maken.</p>

<p><strong>Wij verzoeken u het openstaand bedrag van {totalDebt} binnen de 5 kalenderdagen te voldoen</strong>, met vermelding van de gestructureerde mededeling die op de facturen is opgenomen.</p>

<p>Indien betaling binnen voornoemde termijn uitblijft en zonder gemotiveerde betwisting van uw kant, zien wij ons genoodzaakt het dossier <strong>{ticketId}</strong> in een verdere invorderingsfase te plaatsen. Conform het Burgerlijk Wetboek (artikel 1231/6 e.v.) en de geldende rechtspraak kunnen daarbij <strong>nalatigheidsintresten, een forfaitair schadebeding en bijkomende invorderingskosten</strong> in rekening worden gebracht. Vervolgens kan het dossier worden overgemaakt aan een <strong>gerechtsdeurwaarder of advocaat</strong> met het oog op een gerechtelijke procedure voor de Vrederechter, waarbij eveneens de gerechtskosten en rechtsplegingsvergoeding ten uwen laste zullen vallen.</p>

<p>Wij willen u oprecht aanmoedigen om dit verdere traject te vermijden. Indien u door omstandigheden niet in de mogelijkheid bent om het volledige bedrag onmiddellijk te voldoen, nodigen wij u uit om binnen dezelfde termijn contact met ons op te nemen, zodat wij samen een redelijke <strong>afbetalingsregeling</strong> kunnen uitwerken. Een open communicatie wordt steeds geapprecieerd en voorkomt onnodige kosten en juridische stappen.</p>

<p><em>Een bijkomende afbetalingsregeling is alleen mogelijk indien u nog geen lopende afbetalingsplannen heeft.</em></p>

<p>Voor vragen omtrent de facturen of voor het bespreken van een betalingsregeling kan u ons bereiken via <a href="mailto:incasso@yally.be">incasso@yally.be</a>.</p>

<p>Wij rekenen op uw stipte medewerking en danken u alvast voor de spoedige regeling van dit dossier.</p>

<p>Met de meeste hoogachting,<br/>
<strong>Dienst Debiteurenbeheer — Yally</strong><br/>
<a href="mailto:incasso@yally.be">incasso@yally.be</a></p>`,
    templateType: 'Incasso',
    label: 'Eerste betalingsherinnering',
    description:
      'Professionele eerste herinnering met aankondiging van mogelijke kosten, intresten en juridische procedure indien betaling uitblijft. Vergeet niet de facturen toe te voegen als bijlage.',
  },
  {
    id: 'incasso_final_notice',
    subject: 'INGEBREKESTELLING — laatste aanmaning [{ticketId}]',
    content: `<p>Geachte {contact.firstName} {contact.lastName},</p>

<p>Niettegenstaande onze eerdere herinneringen stellen wij vast dat het openstaand saldo van <strong>{totalDebt}</strong> met betrekking tot het pand <strong>{buildingName}</strong> tot op heden onbetaald is gebleven. De betrokken facturen vindt u opnieuw in bijlage.</p>

<p>Beschouw dit schrijven dan ook als een <strong>formele ingebrekestelling</strong> in de zin van artikel 1139 van het Burgerlijk Wetboek.</p>

<p><strong>Wij sommeren u het volledige openstaand bedrag van {totalDebt} te voldoen binnen een termijn van 5 kalenderdagen</strong>, te rekenen vanaf de verzending van deze ingebrekestelling.</p>

<p>Bij gebreke aan betaling binnen voornoemde termijn zal het dossier <strong>{ticketId}</strong> zonder verdere verwittiging worden overgemaakt aan onze raadsman of aan een gerechtsdeurwaarder, met het oog op gedwongen tenuitvoerlegging. In dat geval zullen, naast de hoofdsom, eveneens de volgende bedragen in rekening worden gebracht:</p>

<ul>
<li>nalatigheidsintresten aan de wettelijke intrestvoet, te rekenen vanaf de vervaldag van iedere factuur;</li>
<li>een forfaitair schadebeding van 10% van de hoofdsom, met een minimum van 75 EUR;</li>
<li>de invorderings-, deurwaarders- en gerechtskosten;</li>
<li>de rechtsplegingsvergoeding zoals bepaald bij Koninklijk Besluit.</li>
</ul>

<p>Daarnaast behouden wij ons het recht voor om, in voorkomend geval, de huurovereenkomst gerechtelijk te laten ontbinden lastens uzelf en de uitzetting te vorderen voor de Vrederechter.</p>

<p>Wij benadrukken nogmaals dat het in uw belang is om dit verdere traject te vermijden. Indien een onmiddellijke betaling van het volledige bedrag niet haalbaar is, verzoeken wij u <strong>dringend</strong> binnen dezelfde termijn van 5 kalenderdagen contact op te nemen met onze dienst Debiteurenbeheer, zodat een schriftelijke <strong>afbetalingsregeling</strong> kan worden vastgelegd. Bij stipte naleving daarvan zien wij af van verdere juridische stappen.</p>

<p>U kan ons bereiken via <a href="mailto:incasso@yally.be">incasso@yally.be</a>.</p>

<p>Indien deze ingebrekestelling kruist met uw betaling, mag u dit schrijven als onbestaande beschouwen.</p>

<p>Met de meeste hoogachting,<br/>
<strong>Dienst Debiteurenbeheer — Yally</strong><br/>
<a href="mailto:incasso@yally.be">incasso@yally.be</a></p>`,
    templateType: 'Incasso',
    label: 'Ingebrekestelling (laatste aanmaning)',
    description:
      'Formele ingebrekestelling vóór overdracht aan deurwaarder/advocaat. Vermeldt forfaitair schadebeding, nalatigheidsintresten, gerechtskosten en mogelijke ontbinding van de huurovereenkomst.',
  },
  {
    id: 'incasso_payment_plan_proposal',
    subject: 'Voorstel afbetalingsregeling [{ticketId}]',
    content: `<p>Geachte {contact.firstName} {contact.lastName},</p>

<p>Naar aanleiding van het openstaand saldo van <strong>{totalDebt}</strong> met betrekking tot het pand <strong>{buildingName}</strong> en uw verzoek tot het bekomen van een afbetalingsregeling, doen wij u onderstaand voorstel toekomen.</p>

<p><strong>Belangrijk:</strong> een bijkomende afbetalingsregeling is alleen mogelijk indien u nog geen lopende afbetalingsplannen heeft. Indien er reeds een lopend afbetalingsplan bestaat, dient dit eerst integraal en stipt te worden afgerond vooraleer een nieuw plan kan worden toegekend.</p>

<p><strong>Voorgestelde regeling:</strong></p>
<ul>
<li>Totaal te vereffenen bedrag: <strong>{totalDebt}</strong></li>
<li>Aantal maandelijkse aflossingen: [AANTAL IN TE VULLEN]</li>
<li>Maandelijks bedrag: [BEDRAG IN TE VULLEN]</li>
<li>Eerste vervaldag: [DATUM IN TE VULLEN]</li>
<li>Te betalen op rekening: [IBAN IN TE VULLEN]</li>
<li>Gestructureerde mededeling: [MEDEDELING IN TE VULLEN]</li>
</ul>

<p>Deze afbetalingsregeling komt <strong>bovenop</strong> uw lopende maandelijkse huur, die uiteraard verder tijdig en integraal dient betaald te worden op de gebruikelijke vervaldag.</p>

<p>Wij wijzen er u uitdrukkelijk op dat dit voorstel slechts geldt onder volgende voorwaarden:</p>
<ul>
<li>strikte en stipte naleving van iedere vervaldag van de regeling;</li>
<li>tijdige betaling van iedere toekomstige maandhuur;</li>
<li>bij niet-naleving van één van bovenstaande voorwaarden vervalt de regeling van rechtswege en wordt het volledige openstaand saldo onmiddellijk opeisbaar, vermeerderd met nalatigheidsintresten, schadebeding en kosten, en wordt het dossier <strong>{ticketId}</strong> zonder verdere verwittiging overgemaakt aan een gerechtsdeurwaarder of advocaat.</li>
</ul>

<p>Gelieve ons binnen de 5 kalenderdagen schriftelijk te bevestigen of u akkoord gaat met bovenstaand voorstel, zodat wij dit definitief in ons dossier kunnen vastleggen. Bij gebreke aan tijdig akkoord, vervalt dit voorstel en hervatten wij de gewone invorderingsprocedure.</p>

<p>Voor vragen of bijsturing van de regeling kan u ons bereiken via <a href="mailto:incasso@yally.be">incasso@yally.be</a>.</p>

<p>Met de meeste hoogachting,<br/>
<strong>Dienst Debiteurenbeheer — Yally</strong><br/>
<a href="mailto:incasso@yally.be">incasso@yally.be</a></p>`,
    templateType: 'Incasso',
    label: 'Voorstel afbetalingsregeling',
    description:
      'Voorstel van afbetalingsplan met de standaard juridische voorwaarden (vervalclausule, opeisbaarheid bij wanbetaling). Vul het aantal aflossingen, bedrag en eerste vervaldag aan vooraleer te verzenden.',
  },
];
